import { useEffect, useRef, useState } from "react";
import { sanitizeHtmlString } from "@/utils";
import Button from "../Button";
import styles from "./ExpandableText.module.scss";

interface props {
  text?: string;
  /** when provided, renders HTML instead of text (used for formatted FROG responses) */
  html?: string;
  className?: string;
  lines?: number;
  fontSize?: number;
  label?: string;
}

export default function ExpandableText({
  text = "",
  html,
  className,
  lines = 2,
  fontSize = 12,
  label,
}: props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isExpandable, setIsExpandable] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const lineHeight = fontSize + 6;
  const content = html !== undefined ? html : text;

  // reset expanded when content changes so new content starts collapsed
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset expanded when content changes
  useEffect(() => {
    setIsExpanded(false);
  }, [content]);

  // apply clamp when collapsed; measure after layout to detect if content is truncated
  useEffect(() => {
    setIsExpandable(false);
    if (!content) return;
    const maxHeight = lines * lineHeight;
    const id = requestAnimationFrame(() => {
      const el = containerRef.current;
      if (!el) return;
      // measure with clamp applied (next frame): scrollHeight is full content height
      if (el.scrollHeight > maxHeight) {
        setIsExpandable(true);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [content, lines, lineHeight]);

  const showClamp = !isExpanded;

  return (
    <div
      className={styles.expandableTextContainer}
      style={{ fontSize: `${fontSize}px`, lineHeight: `${lineHeight}px` }}
    >
      <div
        ref={containerRef}
        className={className}
        style={{
          wordBreak: "break-word",
          ...(showClamp
            ? {
                display: "-webkit-box",
                WebkitLineClamp: lines,
                lineClamp: lines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : {}),
        }}
      >
        {label ? <span className={styles.label}>{label} </span> : null}
        {html !== undefined ? (
          <span
            // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized html from our formatter
            dangerouslySetInnerHTML={{
              __html: sanitizeHtmlString(html),
            }}
          />
        ) : (
          text
        )}
      </div>
      {isExpandable && (
        <Button
          variant="link"
          size="sm"
          className={styles.expandButton}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Show Less" : "Show More"}
        </Button>
      )}
    </div>
  );
}
